import { X, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface ValidationIssue {
  id: string;
  nodeId?: string;
  edgeId?: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  category: 'structure' | 'configuration' | 'logic' | 'performance';
}

interface ValidationPanelProps {
  issues: ValidationIssue[];
  onIssueClick: (issue: ValidationIssue) => void;
  onClose: () => void;
}

const severityConfig = {
  error: {
    icon: AlertTriangle,
    color: 'text-accent-red',
    bgColor: 'bg-accent-red-light dark:bg-accent-red-dark',
    borderColor: 'border-accent-red',
    label: 'Error',
  },
  warning: {
    icon: AlertCircle,
    color: 'text-accent-yellow',
    bgColor: 'bg-accent-yellow-light dark:bg-accent-yellow-dark',
    borderColor: 'border-accent-yellow',
    label: 'Warning',
  },
  info: {
    icon: Info,
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue-light dark:bg-accent-blue-dark',
    borderColor: 'border-accent-blue',
    label: 'Info',
  },
};

export function ValidationPanel({ issues, onIssueClick, onClose }: ValidationPanelProps) {
  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const infoCount = issues.filter(i => i.severity === 'info').length;

  // Group issues by category
  const issuesByCategory = issues.reduce((acc, issue) => {
    if (!acc[issue.category]) {
      acc[issue.category] = [];
    }
    acc[issue.category].push(issue);
    return acc;
  }, {} as Record<string, ValidationIssue[]>);

  return (
    <div className="space-y-2">
      {/* Content */}
      <div className="space-y-2">
        {issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-accent-green-light dark:bg-accent-green-dark flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-accent-green" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-0.5">
                Flow is Valid!
              </h4>
              <p className="text-xs text-muted-foreground">
                No validation issues detected. Your flow is ready to run.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(issuesByCategory).map(([category, categoryIssues]) => (
              <div key={category}>
                <h4 className="text-xs font-semibold text-foreground uppercase mb-1.5">
                  {category}
                </h4>
                <div className="space-y-1.5">
                  {categoryIssues.map((issue) => {
                    const config = severityConfig[issue.severity];
                    const Icon = config.icon;
                    return (
                      <div
                        key={issue.id}
                        onClick={() => onIssueClick(issue)}
                        className={cn(
                          "flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-all duration-200",
                          config.bgColor,
                          config.borderColor,
                          "hover:shadow-md hover:scale-[1.01]"
                        )}
                      >
                        <Icon className={cn("w-4 h-4 flex-shrink-0 mt-0.5", config.color)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground">
                            {issue.message}
                          </p>
                          {(issue.nodeId || issue.edgeId) && (
                            <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                              {issue.nodeId && `Node: ${issue.nodeId}`}
                              {issue.edgeId && `Edge: ${issue.edgeId}`}
                            </p>
                          )}
                        </div>
                        <Badge
                          variant={
                            issue.severity === 'error' ? 'destructive' :
                            issue.severity === 'warning' ? 'outline' :
                            'secondary'
                          }
                          className={
                            issue.severity === 'warning' ? 'border-accent-yellow text-accent-yellow' : ''
                          }
                        >
                          {config.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

